import { useEffect, useRef } from 'preact/hooks';
import Plotly from 'plotly.js';
import { useTheme } from '../../contexts/ThemeContext';
import { getChartTitleColor, resolveSeriesColor } from '../../themes/chartThemeColors';
import { plotlyConfig, plotlyFontSizes, plotlyTransparentBg, simpleLineChartConfig } from './config';
import '../../css/simpleLineChart.css';

export interface SimpleLineChartDataPoint {
  x: string | number | Date;
  y: number;
}

export interface SimpleLineChartProps {
  data: SimpleLineChartDataPoint[];
  title?: string;
  xAxisLabel?: string;
  yAxisLabel?: string;
  lineColor?: string;
  lineWidth?: number;
  showMarkers?: boolean;
  showGrid?: boolean;
  height?: number | string;
  width?: string | number;
  className?: string;
  loading?: boolean;
  markerSize?: number;
  currencyCode?: string;
}

export const SimpleLineChart = ({
  data,
  title = '',
  xAxisLabel = '',
  yAxisLabel = '',
  lineColor,
  lineWidth = simpleLineChartConfig.line.width,
  showMarkers = simpleLineChartConfig.marker.show,
  showGrid = simpleLineChartConfig.grid.show,
  height = simpleLineChartConfig.defaults.height,
  width = simpleLineChartConfig.defaults.width,
  className = '',
  loading = false,
  markerSize = simpleLineChartConfig.marker.size,
  currencyCode
}: SimpleLineChartProps) => {
  const { theme } = useTheme();
  const plotRef = useRef<HTMLDivElement>(null);
  const cfg = simpleLineChartConfig;
  const sizes = plotlyFontSizes;

  useEffect(() => {
    if (!plotRef.current) return;

    if (loading) {
      if (plotRef.current) {
        plotRef.current.innerHTML = '';
      }
      return;
    }

    if (!data || data.length === 0) {
      if (plotRef.current) {
        plotRef.current.innerHTML = '';
      }
      return;
    }

    const xValues = data.map(point => point.x);
    const yValues = data.map(point => point.y);

    const chartLineColor = resolveSeriesColor(
      theme,
      lineColor ?? cfg.line.defaultColor
    );
    const axisColor = resolveSeriesColor(theme, cfg.colors.axis);
    const gridColor = resolveSeriesColor(theme, cfg.colors.grid);
    const seriesName = yAxisLabel || cfg.plot.defaultSeriesName;
    const xLabel = xAxisLabel || cfg.plot.defaultXLabel;

    const plotData: Plotly.Data[] = [
      {
        type: 'scatter',
        mode: showMarkers ? 'lines+markers' : 'lines',
        x: xValues,
        y: yValues,
        name: seriesName,
        line: {
          color: chartLineColor,
          width: lineWidth,
          shape: cfg.line.shape,
          smoothing: cfg.line.smoothing
        },
        marker: showMarkers ? {
          size: markerSize,
          color: chartLineColor
        } : undefined,
        hovertemplate: `<b>${seriesName}:</b> %{y:${cfg.plot.hoverYFormat}}<br><b>${xLabel}:</b> %{x}<extra></extra>`,
        showlegend: cfg.layout.showLegend
      }
    ];

    const margins = {
      ...cfg.layout.margins.withTitle,
      ...(xAxisLabel ? { b: cfg.layout.margins.withTitleAndXLabel.b } : {}),
      ...(yAxisLabel ? { l: cfg.layout.margins.withYLabel.l } : {}),
    };

    const layout: Partial<Plotly.Layout> = {
      title: title ? {
        text: title.toUpperCase(),
        font: {
          family: theme.typography.fontFamily,
          size: sizes.titleFontSize,
          color: getChartTitleColor(theme)
        },
        x: 0,
        xanchor: 'left',
        y: 1,
        yanchor: 'top'
      } : undefined,
      xaxis: {
        title: undefined,
        showgrid: showGrid,
        gridcolor: showGrid ? gridColor : 'transparent',
        tickfont: {
          family: theme.typography.fontFamily,
          size: sizes.tickFontSize,
          color: axisColor
        },
        nticks: Math.min(
          cfg.layout.maxTicks,
          Math.max(cfg.layout.minTicks, Math.ceil(data.length / 2))
        ),
        tickangle: cfg.layout.tickangle
      },
      yaxis: {
        title: yAxisLabel ? {
          text: currencyCode ? `${yAxisLabel} (${currencyCode})` : yAxisLabel,
          font: {
            family: theme.typography.fontFamily,
            size: sizes.tickFontSize,
            color: axisColor
          }
        } : undefined,
        showgrid: showGrid,
        gridcolor: showGrid ? gridColor : 'transparent',
        tickformat: cfg.layout.yTickformat,
        tickfont: {
          family: theme.typography.fontFamily,
          size: sizes.tickFontSize,
          color: axisColor
        }
      },
      font: {
        family: theme.typography.fontFamily,
        color: resolveSeriesColor(theme, cfg.colors.text)
      },
      ...plotlyTransparentBg,
      margin: margins,
      autosize: true,
      height: typeof height === 'number' ? height : undefined
    };

    Plotly.newPlot(plotRef.current, plotData, layout, plotlyConfig)
      .catch((error: unknown) => {
        console.error('Error plotting simple line chart:', error);
      });

    const handleResize = () => {
      if (plotRef.current) {
        Plotly.Plots.resize(plotRef.current);
      }
    };

    window.addEventListener('resize', handleResize);

    let resizeObserver: ResizeObserver | undefined;
    if (height === '100%' && plotRef.current?.parentElement) {
      resizeObserver = new ResizeObserver(handleResize);
      resizeObserver.observe(plotRef.current.parentElement);
    }

    return () => {
      window.removeEventListener('resize', handleResize);
      resizeObserver?.disconnect();
      if (plotRef.current) {
        Plotly.purge(plotRef.current);
      }
    };
  }, [data, title, xAxisLabel, yAxisLabel, lineColor, lineWidth, showMarkers, showGrid, height, theme, loading, markerSize, currencyCode]);

  return (
    <div className={`simple-line-chart-container ${className}`}>
      {loading && (
        <div className="chart-loading-state">Loading...</div>
      )}
      <div 
        ref={plotRef}
        className="simple-line-chart"
        style={{
          width: typeof width === 'string' && width === '100%' ? '100%' : width,
          height: typeof height === 'string' && height === '100%' ? '100%' : height,
          minHeight: typeof height === 'string' ? undefined : `${height}px`
        }}
      />
    </div>
  );
};
